import uuidv4 from 'uuid/v4';
import initialState from '../initialstate';
import * as types from '../../actions/actiontypes';
import {AssignmentStatusIds, HttpStatusCodes} from '../../util/uiconstants';

/**
 * This reducer function handles all the actions related to the top-level objects.
 * @param {Object} state - A portion of the overall Redux state, specifically the
 *      store.<%=names.camel%> object, representing the current data.
 * @param {Object} action - Action object containing type and other values
 * @return {*} current state or array of language objects
 */
export default function statusAssigmentsReducer(state = initialState.statusAssigments, action) {
	switch (action.type) {
		case types.GET_STATUS_ASSIGNMENTS: {
			let statusAssigments = action.response;
			return _.cloneDeep(statusAssigments);
		}
		case types.CLEAR_STATUS_ASSIGNMENTS:
		case types.RESET_TASK_DETAIL:
		case types.RESET_TASK_ATTRIBUTES: {
			return null;
		}
		case types.CREATE_TASK_ASSIGNMENT: {
			if (action.responseObj.status !== HttpStatusCodes.Ok) return state;

			if (action.silentRefreshData?.isFromTaskDetails) {
				let assignments = _.cloneDeep(state);
				const newTA =
					!_.isEmpty(action.response) && action.response[0]
						? action.response[0]
						: {
							assignedToRoleId: action.meta.api.data.engagementRoleId,
							assignedToUserId: action.meta.api.data.userId,
							assignmentId: action.meta.api.data.assignmentId,
							dueDays: action.meta.api.data.dueDays,
							isCurrent: !assignments.reviewersList.some((item) => !item.isComplete),
							taskAssignmentId: uuidv4()
						  };

				const addedTaskAssignment = {
					assignedToRoleId: newTA.assignedToRoleId,
					assignedToUserId: newTA.assignedToUserId,
					assignmentId: newTA.assignmentId,
					assignmentName: action.silentRefreshData.assignmentName,
					assignmentTypeId: action.silentRefreshData.assignmentTypeId,
					displayFullname: action.silentRefreshData.displayFullname,
					displayOrder: 0,
					dueDate: action.silentRefreshData.dueDate,
					dueDays: newTA.dueDays,
					firstName: action.silentRefreshData.firstName,
					isComplete: false,
					isCurrent: newTA.isCurrent,
					isEngagementRoleAssignment: false,
					isPreparer: false,
					isTaskHandedOffToOutOfBoundUser: false,
					lastName: action.silentRefreshData.lastName,
					modifiedByDate: null,
					modifiedByFirstName: null,
					modifiedByLastName: null,
					modifiedByName: null,
					reviewersList: null,
					taskAssignedToId: 0,
					taskAssignmentId: newTA.taskAssignmentId,
					timephaseDueDate: null,
					userInitials: action.silentRefreshData.userInitials
				};

				assignments.reviewersList.push(addedTaskAssignment);
				assignments = setCurrent(assignments);

				return assignments;
			}
			if (action.silentRefreshData?.isFromBulk) {
				let assignments = _.cloneDeep(state);
				action.responseObj.data.forEach((data) => {
					if (data.assignedToRoleId == null) {
						data.firstName = action.silentRefreshData.summary.firstName;
						data.lastName = action.silentRefreshData.summary.lastName;
					}
					data.assignedtoUserId = data.assignedToUserId;
					data.assignmentStatusId = AssignmentStatusIds.Open;
					data.assignmentTypeId = action.silentRefreshData.summary.assignmentTypeId;
					data.assignmentName = action.silentRefreshData.summary.assignmentName;
				});
				if (assignments == null) {
					return {
						assignmentsCreated: action.responseObj.data
					};
				}

				assignments.assignmentsCreated = action.responseObj.data;
				return assignments;
			}

			return state;
		}
		case types.DELETE_TASK_ASSIGNMENT: {
			if (action.responseObj.status !== HttpStatusCodes.NoContent) return state;

			if (action.silentRefreshData && action.silentRefreshData.isFromTaskDetails) {
				let deleteAssignment = _.cloneDeep(state);
				deleteAssignment.reviewersList = deleteAssignment.reviewersList.filter((a) => {
					return a.taskAssignmentId !== action.silentRefreshData.taskAssignmentId;
				});
				return setCurrent(deleteAssignment);
			}

			return state;
		}
		case types.EDIT_TASK_ASSIGNMENTS: {
			if (action.responseObj.status !== HttpStatusCodes.Ok) return state;

			if (action.isFromTaskDetails) {
				let editAssignment = _.cloneDeep(state);
				editAssignment.reviewersList.forEach((a) => {
					if (a.taskAssignmentId === action.meta.api.data.taskAssignmentId) {
						const edited = action.meta.api.data;
						a.assignedToRoleId = edited.engagementRoleId || edited.toAssignedToRoleId;
						a.assignedToUserId = edited.userId || edited.toAssignedToUserId;
						a.assignmentId = edited.toAssignmentId;
						a.assignmentName = edited.assignmentName;
						a.assignmentTypeId = edited.toAssignmentTypeId;
						a.displayFullname = edited.displayFullname;
						a.dueDate = edited.dueDate;
						a.dueDays = parseInt(edited.dueDays);
						a.createByDate = edited.createByDate;
						a.userInitials = edited.userInitials;
						a.firstName = edited.firstName || '';
						a.lastName = edited.lastName || '';
						a.isComplete = action.response.isComplete;
						a.isCurrent = action.response.isCurrent;
					}
				});

				editAssignment.reviewersList = editAssignment.reviewersList.sort((item1, item2) =>
						item2.dueDays - item1.dueDays !== 0
						? item2.dueDays - item1.dueDays
						: item1.assignmentTypeId - item2.assignmentTypeId !== 0
							? item1.assignmentTypeId - item2.assignmentTypeId
							: new Date(item1.createByDate) - new Date(item2.createByDate)
				);

				return editAssignment;
			}

			return Object.assign({}, state, {
				assignmentsUpdated: true
			});
		}
		case types.CHANGE_TASK_ASSIGNMENT_STATUS: {
			if (action.responseObj.status !== HttpStatusCodes.NoContent) return state;

			let newState = _.cloneDeep(state);
			let currentCompleteIndex = newState.reviewersList.findIndex(
				(item) => item.taskAssignmentId === action.taskAssignmentId
			);
			if (!newState.reviewersList[currentCompleteIndex].isComplete) {
				newState.reviewersList[currentCompleteIndex].isCurrent = false;
				newState.reviewersList[currentCompleteIndex].isComplete = true;
				newState.reviewersList[currentCompleteIndex].modifiedByFirstName = action.modifiedByFirstName;
				newState.reviewersList[currentCompleteIndex].modifiedByLastName = action.modifiedByLastName;
				newState.reviewersList[currentCompleteIndex].modifiedByDate = new Date();
			} else {
				newState.reviewersList[currentCompleteIndex].isComplete = false;
				newState.reviewersList[currentCompleteIndex].modifiedByFirstName = action.modifiedByFirstName;
				newState.reviewersList[currentCompleteIndex].modifiedByLastName = action.modifiedByLastName;
				newState.reviewersList[currentCompleteIndex].modifiedByDate = new Date();
			}

			newState = setCurrent(newState);

			return newState;
		}
		case types.GET_TASK_DOCUMENTS:
		case types.GET_TASK_DOCUMENTS_STATUS_FLAGS_ONLY: {
			let currentCompleteIndex = 0;
			if (
				action.payload &&
				action.payload.updateAssignment &&
				state.reviewersList.length > 0 &&
				action.response.taskStatusId == 1 &&
				state.reviewersList[currentCompleteIndex].isComplete
			) {
				// Refresh assignment with firstassignement as isComplete false when we delete document
				let updateState = {
					...state,
					reviewersList: state.reviewersList.map((item, index) => {
						return index == 0
							? {
								...item,
								isComplete: false
							  }
							: item;
					})
				};
				return updateState;
			}
			return state;
		}
		case types.UPDATE_ASSIGNMENTS_TIME_PHASE: {
			let newState = _.cloneDeep(state);
			newState.taskTimePhase = action.timephase;
			return newState;
		}
		default:
			return state;
	}
}

const setCurrent = (newState) => {
	if (!newState.reviewersList.some((item) => item.isCurrent)) {
		let newCurrent = newState.reviewersList
			.sort((item1, item2) =>
					item2.dueDays - item1.dueDays !== 0
					? item2.dueDays - item1.dueDays
					: item1.assignmentTypeId - item2.assignmentTypeId !== 0
						? item1.assignmentTypeId - item2.assignmentTypeId
						: new Date(item1.createByDate) - new Date(item2.createByDate)
			)
			.filter((item) => !item.isComplete)[0];
		if (newCurrent) {
			newCurrent.isCurrent = true;
		}
	}
	return newState;
};
